// De in-chat slide-in (Chat 2.0, besluit B5): een kaart, lijstrij, pill of
// verwijzing opent zijn detail BINNEN het chatvenster — het gesprek blijft
// zichtbaar en je verliest je plek niet. "← Terug naar het antwoord" bovenin,
// de afslag naar het tabblad blijft als expliciete knop bestaan (in de
// detailpanelen zelf en in de afslagen onder het antwoord).
//
// BESLUITGESCHIEDENIS: B8 (27-08) maakte hier tijdelijk een body-portal van
// die met eigen maat óver de chat lag en buiten de modalrand uitstak; de
// owner draaide dat op 28-08 terug — "de slide-in moet in de popup gebeuren,
// niet er overheen". Het paneel is dus weer een laag bínnen de chatmodal
// (de modal-doos is position:relative met overflow:hidden, dus een absolute
// laag kan er per definitie niet uit ontsnappen, transform of niet).
//
// HERGEBRUIK, GEEN NABOUW: de bestaande detailpanelen (SkillDetail,
// TrendDetail, FundingDetail, SourceDetail) zijn pure presentatiecomponenten
// die een gehydrateerde entiteit aannemen. Dit bestand doet alleen wat de
// pagina's ook doen: ophalen, en het juiste paneel voeden.

import { useEffect, useState } from "react";

import type {
  FundingDetailResponse,
  SkillDetailResponse,
  SourceDetailResponse,
  TrendDetailResponse,
} from "../contracts";
import { api } from "../lib/api";
import { color, size, weight } from "./chatTokens";
import type { PaneelDoel } from "./ChatBlokken";
import { FundingDetail } from "./FundingDetail";
import { SkillDetail } from "./SkillDetail";
import { SourceDetail } from "./SourceDetail";
import { TrendDetail } from "./TrendDetail";
import { useResource } from "../lib/useResource";
import { KaartLaag, KaartOpDePlek } from "../nieuw/kaartlaag";
import { LaagTrendLader } from "../nieuw/trendlaag/kaart";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";

type Data =
  | { soort: "skill"; data: SkillDetailResponse }
  | { soort: "trend"; data: TrendDetailResponse }
  | { soort: "funding"; data: FundingDetailResponse }
  | { soort: "bron"; data: SourceDetailResponse };

export function ChatPaneel({
  doel,
  onClose,
}: {
  doel: PaneelDoel | null;
  onClose: () => void;
}) {
  const [inhoud, setInhoud] = useState<Data | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    setInhoud(null);
    setFout(null);
    if (!doel) return;
    // Trendlaag (schakelaar aan): een trend opent de trendlaag-kaart (ChatLaagTrend),
    // niet het oude trenddetail; dat ophalen is dan overbodig. Een ontwikkeling
    // haalt haar kaart zelf op (ChatLaagOntwikkeling).
    if (doel.soort === "trend" && trendlaagAan()) return;
    if (doel.soort === "ontwikkeling") return;
    let leeft = true;
    const zet = (d: Data) => leeft && setInhoud(d);
    const laad = async () => {
      try {
        if (doel.soort === "skill") {
          zet({ soort: "skill", data: await api.skill(doel.id) });
        } else if (doel.soort === "trend") {
          zet({ soort: "trend", data: await api.trend(doel.id) });
        } else if (doel.soort === "funding") {
          zet({ soort: "funding", data: await api.fundingDetail(doel.id) });
        } else {
          zet({ soort: "bron", data: await api.source(doel.id) });
        }
      } catch {
        if (leeft) setFout("Het detail kon niet worden opgehaald.");
      }
    };
    void laad();
    return () => {
      leeft = false;
    };
  }, [doel]);

  if (!doel) return null;

  // key <-> label per as, uit het detail-endpoint zelf (het draagt `assen`
  // precies zodat een rechtstreeks geopend paneel zijn labels kent).
  const fundingLabels = (assen?: Record<string, { key: string; label: string }[]>) => {
    const kaart: Record<string, Record<string, string>> = {};
    for (const [naam, waarden] of Object.entries(assen ?? {})) {
      kaart[naam] = Object.fromEntries((waarden ?? []).map((w) => [w.key, w.label]));
    }
    return kaart;
  };

  // Besluit-omkering 28-08: een laag BINNEN de chatmodal (die is
  // position:relative met overflow:hidden), niet meer een body-portal die
  // eroverheen lag. In het kleine chatformaat vult het paneel vrijwel de
  // hele modal; in fullscreen is het de vertrouwde rechterflank.
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 20 }}>
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(17,24,39,0.18)" }}
      />
      <div
        className="chat-paneel-in"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(700px, 96%)",
          background: color.surface,
          borderLeft: `1px solid ${color.border}`,
          boxShadow: "-12px 0 32px rgba(0,0,0,0.10)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <button
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: color.surfaceAlt,
            border: "none",
            borderBottom: `1px solid ${color.lineSoft}`,
            padding: "9px 16px",
            font: "inherit",
            fontSize: 12,
            fontWeight: weight.medium,
            color: color.muted,
            cursor: "pointer",
            textAlign: "left",
            flexShrink: 0,
          }}
        >
          ← Terug naar het antwoord
        </button>
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {doel.soort === "ontwikkeling" ? (
            <ChatLaagOntwikkeling id={doel.id} titel={doel.titel} />
          ) : doel.soort === "trend" && trendlaagAan() ? (
            <ChatLaagTrend id={doel.id} />
          ) : fout ? (
            <div style={{ padding: 24, fontSize: size.meta, color: color.soft }}>{fout}</div>
          ) : inhoud === null ? (
            <div style={{ padding: 24, fontSize: size.meta, color: color.faint }}>laden…</div>
          ) : inhoud.soort === "skill" ? (
            <SkillDetail
              skill={inhoud.data.skill}
              trends={inhoud.data.contributingTrends ?? []}
              trendsLoading={false}
              evidence={inhoud.data.skill.evidence ?? []}
              evidenceLoading={false}
              onClose={onClose}
            />
          ) : inhoud.soort === "trend" ? (
            <TrendDetail trend={inhoud.data.trend} onClose={onClose} />
          ) : inhoud.soort === "funding" ? (
            <FundingDetail
              funding={inhoud.data.funding}
              labels={fundingLabels(inhoud.data.assen)}
              onClose={onClose}
            />
          ) : (
            <SourceDetail
              source={inhoud.data.source}
              otherFromPublisher={inhoud.data.otherFromPublisher ?? []}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Met de trendlaag aan: de chat geeft een bevinding-id door; /trendlaag/van
 *  vertaalt die naar haar trend, en de chat toont dezelfde trendlaag-kaart als
 *  de kaart-in-kaart (nieuw/trendlaag/kaart.tsx). Hoort de bevinding bij geen
 *  trend, dan het oude detail van die bevinding. */
function ChatLaagTrend({ id }: { id: string }) {
  const van = useResource(() => api.trendlaagVan(id), [id]);
  const oud = useResource(
    () => (van.error || (van.data && !van.data.trendId) ? api.trend(id) : Promise.resolve(null)),
    [id, van.data?.trendId ?? null, van.error ?? null],
  );
  if (van.data?.trendId) {
    return (
      <KaartLaag>
        <div style={{ padding: "20px 24px", display: "grid", gap: 12 }}>
          <LaagTrendLader
            id={van.data.trendId}
            toon={(d) => (
              <>
                <div>{d.kop}</div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: color.ink }}>{d.label}</h2>
                {d.inhoud}
              </>
            )}
          />
        </div>
      </KaartLaag>
    );
  }
  if (oud.data) return <TrendDetail trend={oud.data.trend} onClose={() => {}} />;
  if (oud.error)
    return <div style={{ padding: 24, fontSize: size.meta, color: color.soft }}>Het detail kon niet worden opgehaald.</div>;
  return <div style={{ padding: 24, fontSize: size.meta, color: color.faint }}>laden…</div>;
}

/** CH-A: een ontwikkeling uit "Waar dit op rust" (of haar nummer in de tekst)
 *  opent de ontwikkelingenkaart, dezelfde als de kaart-in-kaart van de
 *  trendkaart (nieuw/trendlaag/kaart.tsx, soort "ontwikkeling"): werkzin, soort
 *  en datum, en de bevindingen met citaat en afzender. KaartOpDePlek zet haar
 *  op deze plek in het paneel, zonder tweede laag eroverheen. */
function ChatLaagOntwikkeling({ id, titel }: { id: string; titel?: string }) {
  return (
    <KaartOpDePlek
      begin={{ soort: "ontwikkeling", id, titel }}
      toon={(d) => (
        <div data-testid="chat-ontwikkeling" style={{ padding: "20px 24px", display: "grid", gap: 12 }}>
          <div>{d.kop}</div>
          {d.inhoud}
          {d.voet}
        </div>
      )}
    />
  );
}
